import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useState,
} from '@wordpress/element';
import { usePricingCalculation } from './usePricingCalculation';
import { useFormFieldDetection } from './useFormFieldDetection';
import { useBookingStore } from '../store/useBookingStore';
import { numberFormat } from '../../helpers/currency';

interface GravityFormsPricingOptions {
	formId: number;
	bookingFieldId: number;
	serviceId: number;
	resourceFieldIds: number[];
}

export function useGravityFormsPricing({
	formId,
	bookingFieldId,
	serviceId,
	resourceFieldIds,
}: GravityFormsPricingOptions) {
	const [isCalculating, setIsCalculating] = useState(false);

	const { calculatePrice, clearCache } = usePricingCalculation({
		formId,
		serviceId,
	});

	const { detectFormFields, getPriceInput } = useFormFieldDetection({
		formId,
		bookingFieldId,
		resourceFieldIds,
	});

	// Read from store instead of individual hooks
	const resourceIds = useBookingStore((state) => state.selectedResourceIds);
	const quantity = useBookingStore((state) => state.quantity);
	const occupancy = useBookingStore((state) => state.occupancy);
	const isInitialized = useBookingStore((state) => state.isInitialized);

	// Recalculate the total on mapped Option changes. GF's modern product
	// handler skips our custom inputType, so its own recalc doesn't fire.
	useEffect(() => {
		const form = document.getElementById(`gform_${formId}`);
		if (!form) {
			return;
		}
		const optionInputs = form.querySelectorAll(
			`.gfield_option_${formId}_${bookingFieldId} input, .gfield_option_${formId}_${bookingFieldId} select`
		);
		const handler = () => window.gformCalculateTotalPrice?.(formId);
		optionInputs.forEach((el) => el.addEventListener('change', handler));
		return () => {
			optionInputs.forEach((el) =>
				el.removeEventListener('change', handler)
			);
		};
	}, [formId, bookingFieldId]);

	const updatePricing = useCallback(
		async (bookingFieldId: number, timeValue?: string): Promise<void> => {
			// Use proper GF types from @gravitywiz/types
			if (!window.gform?.applyFilters) {
				// Fallback for older GF versions
				return;
			}

			// Parse time value
			let startTime: string | undefined;
			let endTime: string | undefined;

			if (timeValue) {
				if (timeValue.includes('|')) {
					[startTime, endTime] = timeValue.split('|');
				} else {
					startTime = timeValue;
					endTime = timeValue;
				}
			}

			// Disable submit button and show loading cursor
			setIsCalculating(true);
			const submitButton = document.querySelector(
				`#gform_submit_button_${formId}`
			) as HTMLButtonElement;
			if (submitButton) {
				submitButton.disabled = true;
				submitButton.style.cursor = 'wait';
			}

			try {
				const calculatedPrice = await calculatePrice({
					resource_ids: resourceIds,
					start_time: startTime,
					end_time: endTime,
					quantity,
					occupancy: occupancy ?? undefined,
					field_id: bookingFieldId,
				});

				// Backend now returns per-unit price (calculated with quantity=1)
				// GF will multiply this by quantity to get the total
				const perUnitPrice = calculatedPrice;

				// Update the booking field price using GF's modern system
				const productData = {
					formId,
					products: {
						products: [
							{
								id: bookingFieldId,
								price: perUnitPrice,
								quantity,
								name: 'Booking',
							},
						],
					},
				};

				// Trigger GF's product_changed filter using proper types
				window.gform.applyFilters(
					'gform/products/product_changed',
					productData
				);

				// Also update the price input directly for immediate feedback
				const priceInput = getPriceInput(bookingFieldId);
				if (priceInput && !priceInput.dataset.updating) {
					priceInput.dataset.updating = 'true';
					priceInput.value = numberFormat(calculatedPrice);
					priceInput.dispatchEvent(
						new Event('change', { bubbles: true })
					);

					// Clean up the updating flag after a microtask
					Promise.resolve().then(() => {
						delete priceInput.dataset.updating;
					});
				}

				// Force GF to recalculate totals
				if (window.gformCalculateTotalPrice) {
					window.gformCalculateTotalPrice(formId);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Failed to update booking pricing:', error);
			} finally {
				// Re-enable submit button and restore cursor
				setIsCalculating(false);
				const submitButton = document.querySelector(
					`#gform_submit_button_${formId}`
				) as HTMLButtonElement;
				if (submitButton) {
					submitButton.disabled = false;
					submitButton.style.cursor = '';
				}
			}
		},
		[
			formId,
			calculatePrice,
			resourceIds,
			quantity,
			occupancy,
			getPriceInput,
		]
	);

	// Trigger pricing update when resourceIds, quantity, or occupancy change
	// Only run after initialization to prevent premature pricing calculations
	useLayoutEffect(() => {
		if (isInitialized && resourceIds.length > 0) {
			// Clear cache when resources, quantity, or occupancy change
			clearCache();

			// Get time field value for pricing calculation
			const { timeFieldElement } = detectFormFields();
			updatePricing(bookingFieldId, timeFieldElement?.value);
		}
	}, [
		isInitialized,
		resourceIds,
		quantity,
		occupancy,
		bookingFieldId,
		updatePricing,
		clearCache,
		detectFormFields,
	]);

	return {
		updatePricing,
		clearCache,
		isCalculating,
	};
}
